/* eslint-disable i18next/no-literal-string */
// @noflow
import { useReactiveVar } from '@apollo/client'
import React from 'react'

import { countryCodeToLocaleCurrency } from '@/utils/countryCodeHelper'
import { formatCurrencyWithDecimal } from '@/utils/currency'

import useWindowSize from '@/hooks/useWindowSize'

import Modal from '@/components/elements/atoms/Modal/Modal'
import Text from '@/components/elements/atoms/Text/Text'
import FreshMealList from '@/components/elements/organisms/FreshMealList/FreshMealList'
import { checkoutPageState } from '@/components/pages/CheckoutPage/CheckoutPage'
import { BREAKPOINTS } from '@/components/templates/Base'

import STYLES from './SelectedRecipesModal.module.sass'

import { checkoutPricingState } from '../../../../hooks/useCheckoutPricing'

type Props = {
  namespace: string
  isOpen: boolean
  toggle: (open: boolean) => void
}

const SelectedRecipesModal = ({
  namespace,
  isOpen,
  toggle
}: Props): JSX.Element => {
  const { windowWidth } = useWindowSize()
  const isMobile = windowWidth <= BREAKPOINTS.sm
  const copyContext = 'order_summary.recipes_modal'

  const { firstOrderPricing } = useReactiveVar(checkoutPricingState)

  const { plan, user } = useReactiveVar(checkoutPageState)
  const { shippingCountryCode, preferredLanguage } = user
  const { dogs } = user

  const { locale, currency } = countryCodeToLocaleCurrency(
    shippingCountryCode,
    preferredLanguage
  )

  const {
    netCoreFoodPrice,
    totalSavedAmount,
    flavourPricings,
    standardOrderCoreFoodGrossPrice
  } = firstOrderPricing

  const savingsApplied = totalSavedAmount > 0

  const orderSummaryMeals =
    flavourPricings?.map((recipe) => {
      return {
        quantity: recipe.servings,
        price: recipe.grossStandardOrderPricePerServing * recipe.servings,
        discountedPrice: recipe.netFirstOrderPricePerServing * recipe.servings,
        flavour: {
          productLabels: [],
          name: recipe.flavourName,
          thumbnail:
            plan.flavours.find(
              ({ flavour }) => flavour.name === recipe.flavourName
            )?.flavour.primaryImage.src || '',
          slug: recipe.flavourName
        }
      }
    }) || []
  return (
    <Modal
      isModalOpen={isOpen}
      setOpenModal={toggle}
      width={450}
      padding={false}
      bottomSticky={isMobile}
    >
      <div className={STYLES.container}>
        <Text
          namespace={namespace}
          text={`${copyContext}.title`}
          variant="textRegular18"
          margin={false}
          align="left"
          bold
          element="h2"
        />
        <FreshMealList
          recipes={orderSummaryMeals}
          config={{ imageSize: 'medium', noBorder: true }}
          shippingCountryCode={shippingCountryCode}
          language={preferredLanguage}
          shouldSeeRecipeSurchargeTooltips={false}
          shouldSeeRecipeSurchargePriceIncreasePl={false}
          perDogLabel={dogs.length > 1}
          priceTextColour="brandBlue500"
          priceOrder="priceFirst"
        />
        <div className={STYLES.dividerLight} />
        <div className={STYLES.pouchesTotal}>
          <Text
            text={`${copyContext}.pouches_total`}
            namespace={namespace}
            margin={false}
            align="left"
            variant="textRegular18"
            colour="brandBlue500"
            bold
            variables={{
              cadenceInDays: plan.durationInDays
            }}
          />
          <div className={STYLES.pouchesPrice}>
            <Text
              text={formatCurrencyWithDecimal(
                savingsApplied
                  ? standardOrderCoreFoodGrossPrice
                  : netCoreFoodPrice,
                {
                  locale,
                  currency
                }
              )}
              margin={false}
              translate={false}
              variant={savingsApplied ? 'textRegular14' : 'textRegular16'}
              colour={savingsApplied ? 'brandBlue400' : 'brandBlue500'}
              bold={!savingsApplied}
              strikeThrough={savingsApplied}
            />
            {savingsApplied && (
              <Text
                text={formatCurrencyWithDecimal(netCoreFoodPrice, {
                  locale,
                  currency
                })}
                margin={false}
                translate={false}
                variant="textRegular16"
                bold
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default SelectedRecipesModal
